import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../services/firebase";
import { Artwork } from "../types/artwork";
import MapView from "../components/MapView";
import { getStatusColor, getStatusText, formatDate } from "../utils";
import OperationHistoryView from "../components/OperationHistoryView";
import "./ArtworkDetails.css";

const ArtworkDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArtwork = async () => {
      if (!id) {
        setError("Invalid artwork ID");
        setLoading(false);
        return;
      }

      try {
        const artworkRef = doc(firestore, "artworks", id);
        const artworkSnap = await getDoc(artworkRef);

        if (artworkSnap.exists()) {
          const data = artworkSnap.data();
          setArtwork({
            id: artworkSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            isDigitized: data.isDigitized || false,
          } as Artwork);
        } else {
          setError("Artwork not found");
        }
      } catch (error) {
        console.error("Error fetching artwork:", error);
        setError("Error loading artwork details");
      } finally {
        setLoading(false);
      }
    };

    fetchArtwork();
  }, [id]);

  // Loading State
  if (loading) {
    return (
      <div className="artwork-details-loading">
        <div className="artwork-details-loading-spinner"></div>
        <p className="artwork-details-loading-text">
          Loading artwork details...
        </p>
      </div>
    );
  }

  // Error State
  if (error || !artwork) {
    return (
      <div className="artwork-details-error">
        <div className="artwork-details-error-content">
          <div className="artwork-details-error-icon">❌</div>
          <h1 className="artwork-details-error-title">
            {error || "Artwork Not Found"}
          </h1>
          <p className="artwork-details-error-description">
            The artwork you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/artworks" className="artwork-details-error-button">
            ← Back to Artworks
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="artwork-details-container">
      {/* Header */}
      <div className="artwork-details-header">
        {/* Decorative elements */}
        <div className="artwork-details-header-decoration-1"></div>
        <div className="artwork-details-header-decoration-2"></div>

        <div className="artwork-details-header-content">
          <div className="artwork-details-header-left">
            <Link to="/artworks" className="artwork-details-back-link">
              <span className="artwork-details-back-arrow">←</span>
              Back to Artworks
            </Link>

            <div className="artwork-details-title-section">
              <span className="artwork-details-title-icon">🎨</span>
              <div className="artwork-details-title-content">
                <h1 className="artwork-details-title">{artwork.title}</h1>
                <p className="artwork-details-artist">by {artwork.artist}</p>
              </div>
            </div>

            {/* Badges */}
            <div className="artwork-details-badges">
              {/* Status Badge */}
              <div className="artwork-details-badge">
                <span
                  className="artwork-details-status-badge"
                  style={{ backgroundColor: getStatusColor(artwork.status) }}
                >
                  <span>
                    {artwork.status === "pending" && "📋"}
                    {artwork.status === "in-progress" && "⚡"}
                    {artwork.status === "obtained" && "🎯"}
                    {artwork.isDigitized && "✅"}
                  </span>
                  {getStatusText(artwork.status)}
                </span>
              </div>

              {/* Digitization Badge */}
              <div className="artwork-details-badge">
                <span
                  className={`artwork-details-digitization-badge ${
                    artwork.isDigitized ? "digitized" : "not-digitized"
                  }`}
                >
                  <span className="artwork-details-digitization-icon">
                    {artwork.isDigitized ? "🖼️" : "📸"}
                  </span>
                  {artwork.isDigitized ? "Digitized" : "Needs Photo"}
                </span>
              </div>
            </div>
          </div>

          <div className="artwork-details-header-right">
            <Link
              to={`/edit-artwork/${id}`}
              className="artwork-details-edit-button"
            >
              <span className="artwork-details-edit-icon">✏️</span>
              Edit Artwork
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="artwork-details-main-content">
        {/* Left Column - Artwork Details */}
        <div className="artwork-details-left-column">
          {/* Artwork Details Card */}
          <div className="artwork-details-card">
            <h2 className="artwork-details-card-title">
              <span className="artwork-details-card-icon">📊</span>
              Artwork Details
            </h2>

            <div className="artwork-details-grid">
              {[
                { label: "Artist", value: artwork.artist, icon: "👨‍🎨" },
                { label: "Location", value: artwork.location, icon: "📍" },
                {
                  label: "Institution",
                  value: artwork.institution,
                  icon: "🏛️",
                },
                { label: "Contact", value: artwork.contact, icon: "📞" },
              ].map((item, index) => (
                <div key={index} className="artwork-details-item">
                  <div className="artwork-details-item-header">
                    <span className="artwork-details-item-icon">
                      {item.icon}
                    </span>
                    <strong className="artwork-details-item-label">
                      {item.label}:
                    </strong>
                  </div>
                  <p className="artwork-details-item-value">{item.value}</p>
                </div>
              ))}

              {/* Digitization Status */}
              <div
                className={`artwork-details-digitization-status ${
                  artwork.isDigitized ? "digitized" : "not-digitized"
                }`}
              >
                <div className="artwork-details-digitization-header">
                  <span className="artwork-details-digitization-icon">
                    {artwork.isDigitized ? "🖼️" : "📸"}
                  </span>
                  <strong
                    className={`artwork-details-digitization-title ${
                      artwork.isDigitized ? "digitized" : "not-digitized"
                    }`}
                  >
                    Digitization Status:
                  </strong>
                </div>
                <div className="artwork-details-digitization-badge-container">
                  <span
                    className={`artwork-details-digitization-badge-status ${
                      artwork.isDigitized ? "digitized" : "not-digitized"
                    }`}
                  >
                    <span>{artwork.isDigitized ? "✅" : "⏳"}</span>
                    {artwork.isDigitized
                      ? "Already Digitized"
                      : "Pending Photography"}
                  </span>
                </div>
                <p
                  className={`artwork-details-digitization-description ${
                    artwork.isDigitized ? "digitized" : "not-digitized"
                  }`}
                >
                  {artwork.isDigitized
                    ? "This artwork has been digitally captured and is available for use."
                    : "This artwork needs digital photography to be completed."}
                </p>
              </div>

              {/* Notes Section */}
              {artwork.notes && (
                <div className="artwork-details-notes">
                  <div className="artwork-details-notes-header">
                    <span className="artwork-details-notes-icon">📝</span>
                    <strong className="artwork-details-notes-label">
                      Notes:
                    </strong>
                  </div>
                  <p className="artwork-details-notes-text">{artwork.notes}</p>
                </div>
              )}

              {/* Images Section */}
              {artwork.images && artwork.images.length > 0 && (
                <div className="artwork-details-images">
                  <div className="artwork-details-images-header">
                    <span className="artwork-details-images-icon">🖼️</span>
                    <strong className="artwork-details-images-label">
                      Artwork Images ({artwork.images.length}):
                    </strong>
                  </div>
                  <div className="artwork-details-images-grid">
                    {artwork.images.map((imageUrl, index) => (
                      <div key={index} className="artwork-details-image-item">
                        <a 
                          href={imageUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="artwork-details-image-link"
                        >
                          <img 
                            src={imageUrl} 
                            alt={`${artwork.title} - Image ${index + 1}`}
                            className="artwork-details-image"
                            loading="lazy"
                          />
                          <div className="artwork-details-image-overlay">
                            <span className="artwork-details-image-overlay-icon">🔍</span>
                            <span className="artwork-details-image-overlay-text">View Full Size</span>
                          </div>
                        </a>
                        <div className="artwork-details-image-actions">
                          <a 
                            href={imageUrl} 
                            download={`${artwork.title}-${index + 1}.jpg`}
                            className="artwork-details-image-download"
                          >
                            <span>📥</span> Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="artwork-details-metadata-grid">
                <div className="artwork-details-metadata-item created">
                  <div className="artwork-details-metadata-header">
                    <span className="artwork-details-metadata-icon">📅</span>
                    <strong className="artwork-details-metadata-label created">
                      Created:
                    </strong>
                  </div>
                  <p className="artwork-details-metadata-value created">
                    {formatDate(artwork.createdAt)}
                  </p>
                </div>

                <div className="artwork-details-metadata-item updated">
                  <div className="artwork-details-metadata-header">
                    <span className="artwork-details-metadata-icon">🔄</span>
                    <strong className="artwork-details-metadata-label updated">
                      Updated:
                    </strong>
                  </div>
                  <p className="artwork-details-metadata-value updated">
                    {formatDate(artwork.updatedAt)}
                  </p>
                </div>
              </div>

              {/* Coordinates */}
              {artwork.coordinates && (
                <div className="artwork-details-coordinates">
                  <div className="artwork-details-coordinates-header">
                    <span className="artwork-details-coordinates-icon">🌍</span>
                    <strong className="artwork-details-coordinates-label">
                      GPS Coordinates:
                    </strong>
                  </div>
                  <div className="artwork-details-coordinates-values">
                    <div className="artwork-details-coordinate-badge">
                      Lat: {artwork.coordinates.lat.toFixed(6)}
                    </div>
                    <div className="artwork-details-coordinate-badge">
                      Lng: {artwork.coordinates.lng.toFixed(6)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Map Section */}
          <div className="artwork-details-map-section">
            <h3 className="artwork-details-map-title">
              <span className="artwork-details-map-title-icon">🗺️</span>
              Location on Map
            </h3>
            <div className="artwork-details-map-container">
              <MapView artworks={[artwork]} />
            </div>
          </div>
        </div>

        {/* Right Column - Operation History */}
        <div>
          <OperationHistoryView artworkId={id ?? ""} />
        </div>
      </div>
    </div>
  );
};

export default ArtworkDetails;
